import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Sparkles, X, Send, Minimize2, Maximize2, Mail,
  AlertCircle, Clock, ChevronRight, Loader, MessageCircle,
  Bell, BellOff, MapPin, Mic, MicOff, FileCheck
} from 'lucide-react';
import api from '../../services/api';
import usePageContext from '../../hooks/usePageContext';
import { useVoiceCommands } from '../../context/VoiceCommandContext';
import { useAuth } from '../../context/AuthContext';
import { usePresence } from '../../context/PresenceContext';

const AstraeaBubble = () => {
  const { user } = useAuth();
  const { emailUnread } = usePresence();
  const canApproveShares = user?.role === 'attorney' || user?.role === 'admin';
  const navigate = useNavigate();
  const location = useLocation();
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const recognitionRef = useRef(null);

  // Page context for AI awareness
  const { context, getContextSummary, pageName } = usePageContext();

  // Global voice commands integration
  const { registerCallback, recognitionSupported: globalVoiceSupported } = useVoiceCommands();

  // Voice input state (local for chat input)
  const [isListening, setIsListening] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(false);

  // UI State
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [hasNotification, setHasNotification] = useState(false);
  const [notificationPulse, setNotificationPulse] = useState(false);

  // Chat State
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [conversationId, setConversationId] = useState(null);

  // Proactive Notification State
  const [proactiveAlert, setProactiveAlert] = useState(null);
  const [lastActivity, setLastActivity] = useState(Date.now());
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [approvalStats, setApprovalStats] = useState(null);
  const [lastApprovalCheck, setLastApprovalCheck] = useState(null);

  // Derive emailStats from WS-driven PresenceContext
  const emailStats = emailUnread;

  // Inactivity threshold (2 minutes)
  const INACTIVITY_THRESHOLD = 2 * 60 * 1000;
  // Approval check interval (30 seconds)
  const APPROVAL_CHECK_INTERVAL = 30 * 1000;

  // Track user activity
  const updateActivity = useCallback(() => {
    setLastActivity(Date.now());
    // If user becomes active, dismiss proactive alert
    if (proactiveAlert && !isOpen) {
      // Keep alert visible for a bit so they can interact
    }
  }, [proactiveAlert, isOpen]);

  // Activity listeners
  useEffect(() => {
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(event => window.addEventListener(event, updateActivity));
    return () => {
      events.forEach(event => window.removeEventListener(event, updateActivity));
    };
  }, [updateActivity]);

  // React to email unread changes from WebSocket (via PresenceContext)
  const prevEmailUnreadRef = useRef(emailUnread?.unread_count);
  useEffect(() => {
    const prev = prevEmailUnreadRef.current;
    const curr = emailUnread?.unread_count || 0;
    prevEmailUnreadRef.current = curr;
    if (prev !== undefined && curr > prev && notificationsEnabled) {
      setHasNotification(true);
      setNotificationPulse(true);
      setTimeout(() => setNotificationPulse(false), 3000);
    }
  }, [emailUnread?.unread_count, notificationsEnabled]);

  // Check for pending approvals periodically (attorneys/admins only)
  const checkForPendingApprovals = useCallback(async () => {
    if (!notificationsEnabled || !canApproveShares) return;

    try {
      const response = await api.get('/documents/shares/pending');
      const pending = response.data || [];
      const pendingCount = pending.length;

      // Compare with last check
      if (lastApprovalCheck !== null && pendingCount > lastApprovalCheck) {
        const newCount = pendingCount - lastApprovalCheck;
        setApprovalStats({
          count: pendingCount,
          newSinceLastCheck: newCount,
          items: pending.slice(0, 3) // Keep first few for display
        });
        setHasNotification(true);
        setNotificationPulse(true);
        setTimeout(() => setNotificationPulse(false), 3000);
      } else {
        setApprovalStats({
          count: pendingCount,
          items: pending.slice(0, 3)
        });
      }

      setLastApprovalCheck(pendingCount);
    } catch (error) {
      console.log('Approval status check failed:', error);
    }
  }, [notificationsEnabled, lastApprovalCheck, canApproveShares]);

  // Check inactivity and trigger proactive alerts
  useEffect(() => {
    const checkInactivity = setInterval(() => {
      const inactiveTime = Date.now() - lastActivity;

      // Don't show alerts if already showing one, chat is open, or notifications disabled
      if (proactiveAlert || isOpen || !notificationsEnabled) return;

      // Priority 1: Show approval alerts (more urgent for attorneys)
      if (
        inactiveTime > INACTIVITY_THRESHOLD &&
        approvalStats?.count > 0 &&
        !location.pathname.includes('/documents')
      ) {
        setProactiveAlert({
          type: 'approval',
          count: approvalStats.count,
          message: approvalStats.count === 1
            ? "You have 1 document awaiting approval"
            : `You have ${approvalStats.count} documents awaiting approval`,
          subMessage: "Client document share requests"
        });
        setHasNotification(true);
        setNotificationPulse(true);
        return;
      }

      // Priority 2: Show email alerts
      if (
        inactiveTime > INACTIVITY_THRESHOLD &&
        emailStats?.unread_count > 0 &&
        !location.pathname.includes('/mail')
      ) {
        setProactiveAlert({
          type: 'email',
          count: emailStats.unread_count,
          urgent: emailStats.urgent_count || 0,
          message: emailStats.unread_count === 1
            ? "You have 1 unread email"
            : `You have ${emailStats.unread_count} unread emails`,
          subMessage: emailStats.urgent_count > 0
            ? `${emailStats.urgent_count} marked urgent`
            : null
        });
        setHasNotification(true);
        setNotificationPulse(true);
      }
    }, 10000); // Check every 10 seconds

    return () => clearInterval(checkInactivity);
  }, [lastActivity, emailStats, approvalStats, proactiveAlert, isOpen, notificationsEnabled, location.pathname]);

  // Approval check interval
  useEffect(() => {
    checkForPendingApprovals(); // Initial check
    const interval = setInterval(checkForPendingApprovals, APPROVAL_CHECK_INTERVAL);
    return () => clearInterval(interval);
  }, [checkForPendingApprovals]);

  // Initialize speech recognition
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      setSpeechSupported(true);
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'en-US';

      recognitionRef.current.onresult = (event) => {
        const transcript = Array.from(event.results)
          .map(result => result[0].transcript)
          .join('');
        setInputValue(transcript);
      };

      recognitionRef.current.onend = () => {
        setIsListening(false);
      };

      recognitionRef.current.onerror = (event) => {
        console.log('Speech recognition error:', event.error);
        setIsListening(false);
      };
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
    };
  }, []);

  // Toggle voice input
  const toggleListening = () => {
    if (!speechSupported || !recognitionRef.current) return;

    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      setInputValue('');
      recognitionRef.current.start();
      setIsListening(true);
    }
  };

  // Scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Load or create conversation
  const initConversation = async () => {
    try {
      // Try to load recent conversation or create new
      const response = await api.get('/astraea-internal/conversations');
      if (response.data.length > 0) {
        const recent = response.data[0];
        setConversationId(recent.id);
        // Load messages
        const msgResponse = await api.get(`/astraea-internal/conversations/${recent.id}`);
        if (msgResponse.data.messages) {
          setMessages(msgResponse.data.messages.map(m => ({
            id: m.id,
            role: m.role,
            content: m.content,
            timestamp: m.timestamp ? new Date(m.timestamp) : new Date()
          })));
        }
      }
    } catch (error) {
      console.log('Could not load conversation:', error);
    }
  };

  // Send message to AI
  const sendMessage = async () => {
    if (!inputValue.trim() || isTyping) return;

    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: inputValue.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);

    try {
      // Include page context with the message
      const contextSummary = getContextSummary();
      const response = await api.post('/astraea-internal/chat', {
        message: userMessage.content,
        conversation_id: conversationId,
        page_context: contextSummary,
        current_page: pageName || 'Application'
      });

      if (response.data.conversation_id && !conversationId) {
        setConversationId(response.data.conversation_id);
      }

      // Handle navigation commands
      if (response.data.navigate_to) {
        const route = response.data.navigate_to;
        if (route.startsWith('http')) {
          window.open(route, '_blank');
        } else {
          // Use a link click to avoid popup blocker
          const a = document.createElement('a');
          a.href = route;
          a.target = '_blank';
          a.rel = 'noopener';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        }
      }

      const aiMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: response.data.response,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      const errorMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: "I'm having trouble connecting right now. Please try again.",
        timestamp: new Date(),
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  };

  // Handle key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Open chat and initialize
  const handleOpen = () => {
    setIsOpen(true);
    setHasNotification(false);
    setProactiveAlert(null);
    if (messages.length === 0) {
      initConversation();
      // Add welcome message if no history
      setMessages([{
        id: 'welcome',
        role: 'assistant',
        content: "Hi! I'm Astraea, your AI assistant. I can help you with questions about your cases, emails, tasks, and more. How can I assist you today?",
        timestamp: new Date()
      }]);
    }
  };

  // Navigate to mail
  const goToMail = () => {
    setProactiveAlert(null);
    setHasNotification(false);
    setIsOpen(false);
    navigate('/mail');
  };

  // Navigate to documents/approvals
  const goToApprovals = () => {
    setProactiveAlert(null);
    setHasNotification(false);
    setIsOpen(false);
    navigate('/documents');
  };

  // Dismiss proactive alert
  const dismissAlert = () => {
    setProactiveAlert(null);
    setNotificationPulse(false);
  };

  // Format timestamp
  const formatTime = (date) => {
    if (!date || isNaN(date.getTime())) return '';
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Parse message content and render markdown links as clickable
  const renderMessageContent = (content) => {
    // Match markdown links: [text](url)
    const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = linkRegex.exec(content)) !== null) {
      // Add text before the link
      if (match.index > lastIndex) {
        parts.push(content.substring(lastIndex, match.index));
      }
      // Add the link as a clickable element
      const linkText = match[1];
      const linkUrl = match[2];
      parts.push(
        <button
          key={match.index}
          onClick={() => navigate(linkUrl)}
          className="text-cyan-400 hover:text-cyan-300 underline font-medium"
        >
          {linkText}
        </button>
      );
      lastIndex = match.index + match[0].length;
    }

    // Add remaining text after last link
    if (lastIndex < content.length) {
      parts.push(content.substring(lastIndex));
    }

    return parts.length > 0 ? parts : content;
  };

  // Handle voice-initiated question (from "Hey Astraea" or "Ask Astraea...")
  const handleVoiceQuestion = useCallback(async (question) => {
    // Open the bubble if not open
    if (!isOpen) {
      setIsOpen(true);
      setHasNotification(false);
      setProactiveAlert(null);
      if (messages.length === 0) {
        initConversation();
      }
    }

    // Send the question
    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: question,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setIsTyping(true);

    try {
      const contextSummary = getContextSummary();
      const response = await api.post('/astraea-internal/chat', {
        message: question,
        conversation_id: conversationId,
        page_context: contextSummary,
        current_page: pageName || 'Application'
      });

      if (response.data.conversation_id && !conversationId) {
        setConversationId(response.data.conversation_id);
      }

      // Handle navigation commands
      if (response.data.navigate_to) {
        const route = response.data.navigate_to;
        if (route.startsWith('http')) {
          window.open(route, '_blank');
        } else {
          // Use a link click to avoid popup blocker
          const a = document.createElement('a');
          a.href = route;
          a.target = '_blank';
          a.rel = 'noopener';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        }
      }

      const aiMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: response.data.response,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      const errorMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: "I'm having trouble connecting right now. Please try again.",
        timestamp: new Date(),
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  }, [isOpen, messages.length, conversationId, getContextSummary, pageName]);

  // Register callbacks for global voice commands
  useEffect(() => {
    if (registerCallback) {
      // When user says "Hey Astraea"
      registerCallback('onAstraeaWake', () => {
        if (!isOpen) {
          handleOpen();
        }
        // Focus the input after opening
        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.focus();
          }
        }, 100);
      });

      // When user says "Hey Astraea [question]" or "Ask Astraea [question]"
      registerCallback('onAstraeaQuestion', (question) => {
        handleVoiceQuestion(question);
      });
    }
  }, [registerCallback, isOpen, handleVoiceQuestion]);

  // Don't render on login page
  if (location.pathname === '/login') {
    return null;
  }

  return (
    <>
      {/* Proactive Alert Popup */}
      {proactiveAlert && !isOpen && (
        <div className="fixed bottom-24 right-6 z-50 animate-slide-up">
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl shadow-2xl border border-cyan-500/30 p-4 max-w-sm">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-semibold text-white">Astraea</span>
                  <span className="text-xs text-cyan-400">just now</span>
                </div>
                <p className="text-sm text-slate-300 mb-2">{proactiveAlert.message}</p>
                {proactiveAlert.subMessage && (
                  <p className="text-xs text-orange-400 flex items-center gap-1 mb-2">
                    <AlertCircle className="w-3 h-3" />
                    {proactiveAlert.subMessage}
                  </p>
                )}
                <div className="flex items-center gap-2">
                  {proactiveAlert.type === 'approval' ? (
                    <button
                      onClick={goToApprovals}
                      className="flex items-center gap-1 px-3 py-1.5 bg-amber-600 hover:bg-amber-700 rounded-lg text-white text-xs font-medium transition-colors"
                    >
                      <FileCheck className="w-3 h-3" />
                      Review Now
                      <ChevronRight className="w-3 h-3" />
                    </button>
                  ) : (
                    <button
                      onClick={goToMail}
                      className="flex items-center gap-1 px-3 py-1.5 bg-cyan-600 hover:bg-cyan-700 rounded-lg text-white text-xs font-medium transition-colors"
                    >
                      <Mail className="w-3 h-3" />
                      View Inbox
                      <ChevronRight className="w-3 h-3" />
                    </button>
                  )}
                  <button
                    onClick={dismissAlert}
                    className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 rounded-lg text-slate-300 text-xs transition-colors"
                  >
                    Later
                  </button>
                </div>
              </div>
              <button
                onClick={dismissAlert}
                className="text-slate-500 hover:text-slate-300 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Chat Panel */}
      {isOpen && (
        <div
          className={`fixed bottom-6 right-6 z-50 bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl shadow-2xl border border-cyan-500/30 flex flex-col transition-all duration-300 ${
            isExpanded ? 'w-[500px] h-[600px]' : 'w-[380px] h-[500px]'
          }`}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-cyan-500/20">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-white font-semibold">Astraea AI</h3>
                <div className="flex items-center gap-1 text-xs text-cyan-400">
                  <MapPin className="w-3 h-3" />
                  <span>Viewing: {pageName || 'Application'}</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setNotificationsEnabled(!notificationsEnabled)}
                className={`p-2 rounded-lg transition-colors ${
                  notificationsEnabled
                    ? 'text-cyan-400 hover:bg-cyan-500/20'
                    : 'text-slate-500 hover:bg-slate-700'
                }`}
                title={notificationsEnabled ? 'Disable notifications' : 'Enable notifications'}
              >
                {notificationsEnabled ? <Bell className="w-4 h-4" /> : <BellOff className="w-4 h-4" />}
              </button>
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="p-2 text-cyan-400 hover:bg-cyan-500/20 rounded-lg transition-colors"
              >
                {isExpanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="p-2 text-cyan-400 hover:bg-cyan-500/20 rounded-lg transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Quick Actions Bar */}
          {approvalStats && approvalStats.count > 0 && (
            <div
              onClick={goToApprovals}
              className="mx-4 mt-3 p-3 bg-amber-500/10 border border-amber-500/20 rounded-xl cursor-pointer hover:bg-amber-500/20 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FileCheck className="w-4 h-4 text-amber-400" />
                  <span className="text-sm text-amber-300">
                    {approvalStats.count} pending approval{approvalStats.count !== 1 ? 's' : ''}
                  </span>
                </div>
                <ChevronRight className="w-4 h-4 text-amber-400" />
              </div>
            </div>
          )}
          {emailStats && emailStats.unread_count > 0 && (
            <div
              onClick={goToMail}
              className="mx-4 mt-3 p-3 bg-cyan-500/10 border border-cyan-500/20 rounded-xl cursor-pointer hover:bg-cyan-500/20 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-cyan-400" />
                  <span className="text-sm text-cyan-300">
                    {emailStats.unread_count} unread email{emailStats.unread_count !== 1 ? 's' : ''}
                  </span>
                  {emailStats.urgent_count > 0 && (
                    <span className="px-2 py-0.5 bg-orange-500/20 text-orange-400 text-xs rounded-full">
                      {emailStats.urgent_count} urgent
                    </span>
                  )}
                </div>
                <ChevronRight className="w-4 h-4 text-cyan-400" />
              </div>
            </div>
          )}

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                    message.role === 'user'
                      ? 'bg-cyan-600 text-white'
                      : message.isError
                      ? 'bg-red-500/20 text-red-300 border border-red-500/30'
                      : 'bg-slate-700/50 text-slate-200'
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap">{renderMessageContent(message.content)}</p>
                  <p className={`text-xs mt-1 ${
                    message.role === 'user' ? 'text-cyan-200' : 'text-slate-500'
                  }`}>
                    {formatTime(message.timestamp)}
                  </p>
                </div>
              </div>
            ))}

            {/* Typing Indicator */}
            {isTyping && (
              <div className="flex justify-start">
                <div className="bg-slate-700/50 rounded-2xl px-4 py-3">
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <div className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <div className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Listening Indicator Banner */}
          {isListening && (
            <div className="mx-4 mb-2 p-3 bg-gradient-to-r from-blue-500/20 to-cyan-500/20 border border-blue-500/40 rounded-xl animate-pulse-glow">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {/* Animated mic icon with rings */}
                  <div className="relative">
                    <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center">
                      <Mic className="w-4 h-4 text-white" />
                    </div>
                    {/* Pulsing rings */}
                    <div className="absolute inset-0 rounded-full border-2 border-blue-400 animate-ping-slow opacity-75" />
                    <div className="absolute -inset-1 rounded-full border border-blue-400/50 animate-ping-slower opacity-50" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-blue-200">Listening...</p>
                    <p className="text-xs text-blue-300/70">Speak now</p>
                  </div>
                </div>
                {/* Sound wave visualization */}
                <div className="flex items-center gap-0.5 h-6">
                  {[...Array(5)].map((_, i) => (
                    <div
                      key={i}
                      className="w-1 bg-blue-400 rounded-full animate-wave"
                      style={{
                        animationDelay: `${i * 0.1}s`,
                        height: '100%',
                      }}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Input */}
          <div className="p-4 border-t border-cyan-500/20">
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyPress}
                placeholder={isListening ? "Listening..." : "Ask Astraea anything..."}
                className={`flex-1 bg-slate-800/50 border rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:ring-2 resize-none text-sm transition-all duration-300 ${
                  isListening
                    ? 'border-blue-500 ring-2 ring-blue-500/50 shadow-lg shadow-blue-500/20 placeholder-blue-300'
                    : 'border-cyan-500/30 focus:ring-cyan-500'
                }`}
                rows={1}
                style={{ maxHeight: '120px' }}
              />
              {/* Voice Input Button */}
              <div className="relative">
                <button
                  onClick={toggleListening}
                  disabled={isTyping || !speechSupported}
                  className={`p-3 rounded-xl text-white transition-all duration-300 ${
                    isListening
                      ? 'bg-gradient-to-r from-blue-500 to-rose-500 shadow-lg shadow-blue-500/40'
                      : 'bg-slate-700 hover:bg-slate-600'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                  title={!speechSupported ? "Voice input not supported in this browser" : isListening ? "Stop listening" : "Voice input"}
                >
                  {isListening ? (
                    <MicOff className="w-5 h-5" />
                  ) : (
                    <Mic className="w-5 h-5" />
                  )}
                </button>
                {/* Pulsing ring around button when listening */}
                {isListening && (
                  <>
                    <div className="absolute inset-0 rounded-xl border-2 border-blue-400 animate-ping-slow opacity-60" />
                  </>
                )}
              </div>
              <button
                onClick={sendMessage}
                disabled={!inputValue.trim() || isTyping}
                className="p-3 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 disabled:from-slate-600 disabled:to-slate-700 rounded-xl text-white transition-all disabled:cursor-not-allowed"
              >
                {isTyping ? (
                  <Loader className="w-5 h-5 animate-spin" />
                ) : (
                  <Send className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Floating Bubble Button */}
      {!isOpen && (
        <button
          onClick={handleOpen}
          className={`fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-gradient-to-br from-cyan-500 to-blue-500 shadow-lg flex items-center justify-center transition-all hover:scale-110 hover:shadow-cyan-500/50 ${
            notificationPulse ? 'animate-pulse' : ''
          }`}
        >
          <Sparkles className="w-6 h-6 text-white" />

          {/* Notification Badge */}
          {hasNotification && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-white text-xs font-bold animate-bounce">
              {emailStats?.unread_count || '!'}
            </span>
          )}
        </button>
      )}

      {/* Animation Styles */}
      <style>{`
        @keyframes slide-up {
          from {
            transform: translateY(20px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out forwards;
        }

        @keyframes ping-slow {
          0% {
            transform: scale(1);
            opacity: 0.75;
          }
          75%, 100% {
            transform: scale(1.5);
            opacity: 0;
          }
        }
        .animate-ping-slow {
          animation: ping-slow 1.5s cubic-bezier(0, 0, 0.2, 1) infinite;
        }

        @keyframes ping-slower {
          0% {
            transform: scale(1);
            opacity: 0.5;
          }
          75%, 100% {
            transform: scale(2);
            opacity: 0;
          }
        }
        .animate-ping-slower {
          animation: ping-slower 2s cubic-bezier(0, 0, 0.2, 1) infinite;
        }

        @keyframes wave {
          0%, 100% {
            transform: scaleY(0.3);
          }
          50% {
            transform: scaleY(1);
          }
        }
        .animate-wave {
          animation: wave 0.6s ease-in-out infinite;
        }

        @keyframes pulse-glow {
          0%, 100% {
            box-shadow: 0 0 10px rgba(236, 72, 153, 0.3);
          }
          50% {
            box-shadow: 0 0 20px rgba(236, 72, 153, 0.5);
          }
        }
        .animate-pulse-glow {
          animation: pulse-glow 1.5s ease-in-out infinite;
        }
      `}</style>
    </>
  );
};

export default AstraeaBubble;
